<script setup lang="ts">
import Card from 'primevue/card';
import Avatar from 'primevue/avatar';
import Chip from 'primevue/chip';
import type { BetSlipProps, BetSlipEmits, BetSlipItem } from '@/types/betslip.types';

defineProps<BetSlipProps>();

const emit = defineEmits<BetSlipEmits>();

// PrimeVue pass-through constants (extracted to prevent per-render allocation)
const CARD_PT = {
  root: { class: '!rounded-none !shadow-none' },
  body: { class: '!p-6 !pt-4 !pb-6' },
};

const AVATAR_PT = {
  root: { class: '!rounded-xl overflow-hidden' },
};

const CHIP_PT = {
  root: { class: 'border !rounded-none !py-1 !bg-surface-container-lowest' },
  label: { class: 'text-sm' },
};

function handleHeaderClick() {
  emit('header-click');
}

function handleItemClick(item: BetSlipItem) {
  emit('item-click', item);
}
</script>

<template>
  <Card class="!bg-surface-container-lowest overflow-hidden" :pt="CARD_PT">
    <template #header>
      <!-- Header (accessible) -->
      <div
        data-testid="betslip-header"
        class="flex flex-wrap items-center justify-between gap-2 px-6 pt-4 cursor-pointer"
        role="button"
        tabindex="0"
        :aria-label="`View all ${title}`"
        @click="handleHeaderClick"
        @keydown.enter="handleHeaderClick"
        @keydown.space.prevent="handleHeaderClick"
      >
        <span class="text-on-surface md:text-2xl font-semibold">{{ title }}</span>
      </div>
    </template>

    <template #content>
      <!-- Items List -->
      <div class="overflow-hidden flex flex-col gap-2">
        <div
          v-for="item in items"
          :key="item.id"
          :data-testid="`betslip-item-${item.id}`"
          class="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 cursor-pointer bg-surface"
          role="button"
          tabindex="0"
          :aria-label="`Select ${item.title}`"
          @click="handleItemClick(item)"
          @keydown.enter="handleItemClick(item)"
          @keydown.space.prevent="handleItemClick(item)"
        >
          <!-- Optional Avatar Image -->
          <Avatar v-if="item.image" :image="item.image" size="large" :pt="AVATAR_PT" />

          <!-- Title -->
          <span
            class="flex-1 min-w-48 text-xl font-medium"
            :style="item.titleColor ? { color: item.titleColor } : undefined"
          >
            {{ item.title }}
          </span>

          <!-- Chips -->
          <div class="flex gap-2">
            <Chip
              v-for="(chip, idx) in item.chips"
              :key="`${item.id}-${chip.label}`"
              :data-testid="`betslip-item-${item.id}-chip-${idx}`"
              :label="chip.label"
              class="tabular-nums"
              :pt="CHIP_PT"
            />
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>
